import React, { useEffect } from 'react';
import { useNetworks } from '@/hooks/useNetworks';
import { useFiles } from '@/hooks/useFiles';
import { useNetworkUI } from '@/hooks/useNetworkUI';
import { useNetworkContext } from '@/contexts/NetworkContext';
import { useTabContext } from '@/contexts/TabContext';
import { FileType, Tab } from '@/types/data';
import { convertToShippitServiceability } from '@/utils/shippitServiceabilityConverter';

const LeftPanel: React.FC = () => {
  const {
    networks,
    newNetworkName,
    setNewNetworkName,
    addNetwork,
    deleteNetwork,
    deleteFile,
    addFileToNetwork,
    getEditedData,
  } = useNetworks();

  const { currentNetwork, setCurrentNetwork } = useNetworkContext();

  const { activeTab, setActiveTab } = useTabContext();

  // Track the last added network ID to set it as current when networks are updated
  const [lastAddedNetworkId, setLastAddedNetworkId] = React.useState<string | null>(null);

  // Modal state for conversion dialog
  const [showConversionModal, setShowConversionModal] = React.useState(false);
  const [pendingConversion, setPendingConversion] = React.useState<{
    network: (typeof networks)[0];
    file: (typeof networks)[0]['files'][0];
  } | null>(null);

  // When networks change, check if we need to set a newly added network as current
  useEffect(() => {
    if (lastAddedNetworkId) {
      const newNetwork = networks.find((network) => network.id === lastAddedNetworkId);
      if (newNetwork) {
        setCurrentNetwork(newNetwork);
        setLastAddedNetworkId(null); // Reset after setting
      }
    }
  }, [networks, lastAddedNetworkId, setCurrentNetwork]);

  const { fileInputRef, handleFileUpload, triggerFileInput, handleFileInputChange, getFileIcon } =
    useFiles(addFileToNetwork);

  const { dragOverNetworkId, expandNetwork, handleDragOver, handleDragLeave, handleDrop } =
    useNetworkUI();

  // Handler for deleting a network
  const handleDeleteNetwork = (networkId: string) => {
    // If the network being deleted is the current network, clear the current network
    if (currentNetwork && currentNetwork.id === networkId) {
      setCurrentNetwork(null);
    }
    deleteNetwork(networkId);
  };

  // Handler for deleting a file
  const handleDeleteFile = (networkId: string, fileId: string) => {
    deleteFile(networkId, fileId);
  };

  // Helper function to get tab from file type
  const getTabFromFileType = (fileType: FileType): Tab => {
    switch (fileType) {
      case FileType.Hubs:
        return 'hubs';
      case FileType.Serviceability:
        return 'serviceability';
      case FileType.Connections:
        return 'connections';
      case FileType.Visualise:
        return 'visualise';
      default:
        return 'overview';
    }
  };

  // Handler for clicking on a file
  const handleFileClick = (network: (typeof networks)[0], fileType: FileType) => {
    // Map file type to tab
    setCurrentNetwork(network);
    setActiveTab(getTabFromFileType(fileType));
  };

  // Handler for setting the current network
  const handleSetCurrentNetwork = (network: (typeof networks)[0]) => {
    setCurrentNetwork(network);
  };

  // Handler for adding a new network
  const handleAddNetwork = () => {
    const newNetworkId = addNetwork();
    if (newNetworkId) {
      expandNetwork(newNetworkId);
      // Store the ID of the newly created network to set it as current when networks are updated
      setLastAddedNetworkId(newNetworkId);
    }
  };

  // Handler for downloading a file as CSV
  const handleDownloadFile = (network: (typeof networks)[0], file: (typeof network.files)[0]) => {
    // Get the data type from the file type (lowercase)
    const dataType = file.type.toLowerCase();

    // Get edited data from local storage
    const editedData = getEditedData(dataType, network.id, file.id);

    // Use edited data if available, otherwise fall back to original content
    const content = editedData || file.content;

    if (content) {
      // Create a blob from the CSV content
      const blob = new Blob([content], { type: 'text/csv' });

      // Create a URL for the blob
      const url = URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger the download
      const a = document.createElement('a');
      a.href = url;
      // Include network name in the downloaded file name
      a.download = `${network.name}_${file.name}`;
      document.body.appendChild(a);
      a.click();

      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleConvertToShippitServiceability = (
    network: (typeof networks)[0],
    file: (typeof network.files)[0]
  ) => {
    // Only allow conversion for serviceability files
    if (file.type !== FileType.Serviceability) {
      alert('This conversion only works with Serviceability files');
      return;
    }

    // Get the data type from the file type (lowercase)
    const dataType = file.type.toLowerCase();

    // Get edited data from local storage
    const editedData = getEditedData(dataType, network.id, file.id);

    // Use edited data if available, otherwise fall back to original content
    const content = editedData || file.content;

    if (!content) {
      alert('No data available to convert');
      return;
    }

    // Only show modal if network name is "SKU", otherwise use new format by default
    if (network.name === 'SKU') {
      setPendingConversion({ network, file });
      setShowConversionModal(true);
    } else {
      // Use new format directly
      setPendingConversion({ network, file });
      executeConversion(false); // false = use new format
    }
  };

  const executeConversion = (useOldSkuZones: boolean) => {
    if (!pendingConversion) return;

    const { network, file } = pendingConversion;
    const dataType = file.type.toLowerCase();
    const editedData = getEditedData(dataType, network.id, file.id);
    const content = editedData || file.content;

    if (!content) return;

    try {
      const convertedContent = convertToShippitServiceability(content, useOldSkuZones);

      // Create a blob from the converted CSV content
      const blob = new Blob([convertedContent], { type: 'text/csv' });

      // Create a URL for the blob
      const url = URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger the download
      const a = document.createElement('a');
      a.href = url;
      // Generate filename with network name and timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      a.download = `${network.name}_lyn-serviceability_${timestamp}.csv`;
      document.body.appendChild(a);
      a.click();

      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log('✅ Conversion complete! File downloaded.');
    } catch (error) {
      console.error('Error converting serviceability data:', error);
      alert(
        `Error converting serviceability data: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setShowConversionModal(false);
      setPendingConversion(null);
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-md h-full overflow-y-auto">
      <h2 className="text-xl font-semibold text-primary-700 mb-4">Logistic Networks</h2>

      {/* Add new network form */}
      <div className="mb-6">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={newNetworkName}
            onChange={(e) => setNewNetworkName(e.target.value)}
            placeholder="Network name"
            className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            onKeyPress={(e) => e.key === 'Enter' && handleAddNetwork()}
          />
          <button
            onClick={handleAddNetwork}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Add
          </button>
        </div>
      </div>

      {/* Hidden file input for uploads */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        accept=".csv"
        multiple
      />

      {/* Networks list */}
      <div className="space-y-4">
        {networks.length === 0 ? (
          <p className="text-gray-500 italic">
            No networks added yet. Create your first network above.
          </p>
        ) : (
          networks.map((network) => (
            <div key={network.id} className="border border-gray-200 rounded-md overflow-hidden">
              {/* Network header */}
              <div
                className={`flex items-center justify-between p-3 ${
                  currentNetwork?.id === network.id ? 'bg-primary-100' : 'bg-gray-100'
                }`}
              >
                <div className="flex items-center text-left flex-grow font-medium text-gray-800">
                  {network.name}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => triggerFileInput(network.id)}
                    className="text-primary-600 hover:text-primary-800"
                    title="Upload files"
                  >
                    <img src="/upload.svg" alt="Upload files" className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteNetwork(network.id)}
                    className="text-red-600 hover:text-red-800"
                    title="Delete network"
                  >
                    <img src="/trash.svg" alt="Delete" className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Network files */}
              <div
                className={`p-3 ${dragOverNetworkId === network.id ? 'bg-blue-50' : ''}`}
                onDragOver={(e) => handleDragOver(e, network.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, network.id, handleFileUpload)}
              >
                {network.files.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    <p>Drop .csv files here or click upload</p>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {/* Visualise button as first item in the list - only show if this network has a connections file */}
                    {network.files.some((file) => file.type === FileType.Connections) && (
                      <li
                        key="visualise"
                        className={`flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer ${
                          activeTab === 'visualise' && currentNetwork?.id === network.id
                            ? 'bg-primary-100'
                            : 'bg-blue-50'
                        }`}
                        onClick={() => {
                          handleSetCurrentNetwork(network);
                          setActiveTab('visualise');
                        }}
                      >
                        <div className="flex items-center">
                          <span className="mr-2">
                            <img src="/chart.svg" alt="Visualise" className="h-5 w-5" />
                          </span>
                          <span className="text-sm font-medium text-blue-600">
                            Visualise connections
                          </span>
                        </div>
                      </li>
                    )}
                    {/* Test Route button - only show if this network has serviceability, hubs, and connections files */}
                    {network.files.some((file) => file.type === FileType.Serviceability) &&
                      network.files.some((file) => file.type === FileType.Hubs) &&
                      network.files.some((file) => file.type === FileType.Connections) && (
                        <li
                          key="testroute"
                          className={`flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer ${
                            activeTab === 'testroute' && currentNetwork?.id === network.id
                              ? 'bg-primary-100'
                              : 'bg-emerald-50'
                          }`}
                          onClick={() => {
                            handleSetCurrentNetwork(network);
                            setActiveTab('testroute');
                          }}
                        >
                          <div className="flex items-center">
                            <span className="mr-2">
                              <svg
                                className="h-5 w-5 text-emerald-600"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                                />
                              </svg>
                            </span>
                            <span className="text-sm font-medium text-emerald-600">Test route</span>
                          </div>
                        </li>
                      )}
                    {network.files.map((file) => (
                      <li
                        key={file.id}
                        className={`flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer ${
                          activeTab === getTabFromFileType(file.type) &&
                          currentNetwork?.id === network.id
                            ? 'bg-primary-100'
                            : ''
                        }`}
                        onClick={() => handleFileClick(network, file.type)}
                      >
                        <div className="flex items-center">
                          <span className="mr-2">
                            <img src={getFileIcon(file.type)} alt={file.type} className="h-5 w-5" />
                          </span>
                          <span className="text-sm">{file.name}</span>
                        </div>
                        <div className="flex space-x-2">
                          {/* Only show convert button for Serviceability files */}
                          {file.type === FileType.Serviceability && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent triggering the li's onClick
                                handleConvertToShippitServiceability(network, file);
                              }}
                              className="text-purple-600 hover:text-purple-800 text-sm"
                              title="Convert to Shippit Serviceability"
                            >
                              <img
                                src="/file-spreadsheet.svg"
                                alt="Convert to Shippit Serviceability"
                                className="h-4 w-4"
                              />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent triggering the li's onClick
                              handleDownloadFile(network, file);
                            }}
                            className="text-green-600 hover:text-green-800 text-sm"
                            title="Download file as CSV"
                          >
                            <img src="/download.svg" alt="Download file" className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent triggering the li's onClick
                              handleDeleteFile(network.id, file.id);
                            }}
                            className="text-red-600 hover:text-red-800 text-sm"
                            title="Delete file"
                          >
                            <img src="/trash.svg" alt="Delete file" className="h-4 w-4" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Conversion Format Modal */}
      {showConversionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select SKU Zone Format</h3>
            <p className="text-gray-600 mb-6">Choose the format you want to use for SKU zones:</p>
            <div className="space-y-3 mb-6">
              <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                <div className="font-medium text-gray-800 mb-1">Old Format</div>
                <div className="text-sm text-gray-600">NSW-1, NSW-2, VIC-1, etc.</div>
              </div>
              <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                <div className="font-medium text-gray-800 mb-1">New Format</div>
                <div className="text-sm text-gray-600">NCI, NC2, SNO, etc.</div>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => executeConversion(true)}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Old Format
              </button>
              <button
                onClick={() => executeConversion(false)}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                New Format
              </button>
            </div>
            <button
              onClick={() => {
                setShowConversionModal(false);
                setPendingConversion(null);
              }}
              className="w-full mt-3 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md focus:outline-none"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeftPanel;
